/* eslint-disable @next/next/inline-script-id */
import { useEffect } from 'react';
import { ApolloProvider } from '@apollo/client/react';
import { ThemeProvider } from '@material-ui/styles';
import { AppProps } from 'next/dist/shared/lib/router/router';
import Script from 'next/script';
import { config } from '@fortawesome/fontawesome-svg-core';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import '@fortawesome/fontawesome-svg-core/styles.css';

import { apolloClient } from '../utils/apollo/apollo-client';
import { theme } from '../theme';
import { CssBaseline } from '@material-ui/core';
import CartProvider from 'components/cart/CartProvider';
import { useRouter } from 'next/router';
import { googlePageview, googleTagManagerPageView } from 'utils/helpers/analytics/google';
import { facebookPageview } from 'utils/helpers/analytics/facebook';

config.autoAddCss = false;

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();

  useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles) {
      jssStyles?.parentElement?.removeChild(jssStyles);
    }
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      googlePageview(url);
      facebookPageview();
      googleTagManagerPageView(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  return (
    <>
      {/* Global Site Tag (gtag.js) - Google Analytics */}
      <Script
        strategy="afterInteractive"
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}`}
      />
      <Script
        id="gtag-init"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS}', {
              page_path: window.location.pathname,
            });
            gtag('config', '${process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}');
          `
        }}
      />

      {/* Google Tag Manager - Global base code */}
      <Script
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', '${process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID}');
          `
        }}
      />

      {/* Global Site Code Pixel - Facebook Pixel */}
      <Script
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', ${process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID});
            fbq('track', 'PageView');
          `
        }}
      />

      {/* Mailchimp Site Code */}
      <Script
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
          !function(c,h,i,m,p){m=c.createElement(h),p=c.getElementsByTagName(h)[0],m.async=1,m.src=i,p.parentNode.insertBefore(m,p)}(document,"script","https://chimpstatic.com/mcjs-connected/js/users/94bfa8bca31177b876ef5dde9/2717ca9b497df2a9fe6ad76b5.js");
          `
        }}
      />

      <ThemeProvider theme={theme}>
        <ApolloProvider client={apolloClient}>
          <CartProvider>
            <CssBaseline />
            <Component {...pageProps} />
          </CartProvider>
        </ApolloProvider>
      </ThemeProvider>
    </>
  );
};

export default App;
